import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { fetchSchedulesByWeek, fetchMyAssignedShifts } from '../api';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import EnterpriseAnalytics from '../components/EnterpriseAnalytics';
import {
  FiUsers, FiMapPin, FiCalendar, FiClock, FiDollarSign,
  FiAlertCircle, FiTrendingUp, FiCheck, FiX, FiActivity,
  FiChevronLeft, FiChevronRight, FiDownload, FiRefreshCw,
  FiSun, FiUser, FiBarChart2
} from 'react-icons/fi';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subWeeks, subMonths, subYears, addWeeks, addMonths, addYears, addDays, parse, isAfter, isBefore } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, periodSelector, onPeriodChange, onNavigate }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between mb-3">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className={`text-3xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
        )}
        {trend && (
          <div className={`flex items-center mt-2 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <FiTrendingUp className="mr-1" />
            <span>{Math.abs(trend)}% vs last period</span>
          </div>
        )}
      </div>
      <div className={`p-4 rounded-lg ${color ? `${color.replace('text', 'bg')}/10` : 'bg-gray-100'}`}>
        <Icon className={`h-8 w-8 ${color || 'text-gray-600'}`} />
      </div>
    </div>
    {periodSelector && (
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        <div className="flex gap-1">
          {['week', 'month', 'year'].map((p) => (
            <button
              key={p}
              onClick={() => onPeriodChange(p)}
              className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                periodSelector === p
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => onNavigate('prev')}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
          >
            <FiChevronLeft className="h-4 w-4 text-gray-600" />
          </button>
          <button
            onClick={() => onNavigate('next')}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
          >
            <FiChevronRight className="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>
    )}
  </div>
);

const AdminDashboardOverview = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalEmployees: 0,
    totalLocations: 0,
    activeSchedules: 0,
    attendanceRate: 0,
  });
  const [attendanceData, setAttendanceData] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [weeklySchedules, setWeeklySchedules] = useState([]);
  const [todaySchedules, setTodaySchedules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(toZonedTime(new Date(), 'Europe/London'), 'yyyy-MM-dd'));

  // Get today's date
  const today = format(toZonedTime(new Date(), 'Europe/London'), 'yyyy-MM-dd');

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'manager') {
      fetchDashboardData();
      fetchTodayData();
      fetchWeeklySchedules();
      fetchTodaySchedules(selectedDate);
    }
  }, [user, selectedDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      console.log('Fetching dashboard data from:', API_URL);

      const [usersRes, locationsRes, schedulesRes] = await Promise.all([
        axios.get(`${API_URL}/users/all`, { headers }).catch(e => {
          console.error('Users API failed:', e.response?.data || e.message);
          return { data: {} };
        }),
        axios.get(`${API_URL}/api/locations`, { headers }).catch(e => {
          console.error('Locations API failed:', e.response?.data || e.message);
          return { data: { data: [] } };
        }),
        fetchSchedulesByWeek(today).catch(e => {
          console.error('Schedules API failed:', e.message);
          return { data: { data: [] } };
        }),
      ]);

      console.log('API Responses:', {
        users: usersRes.data,
        locations: locationsRes.data,
        schedules: schedulesRes.data,
      });

      // Calculate real counts
      // API returns: { success: true, count: X, data: [...] }
      let users = [];
      if (usersRes.data?.data && Array.isArray(usersRes.data.data)) {
        users = usersRes.data.data;
      } else if (Array.isArray(usersRes.data)) {
        users = usersRes.data;
      }
      
      console.log('Total users fetched:', users.length, 'Full response:', usersRes.data);
      console.log('Users array:', users);
      
      // Count users with role employee, careWorker, or access field set to 'user' or 'employee'
      const totalEmployees = users.filter(u => 
        u.role === 'employee' || 
        u.role === 'careWorker' || 
        u.role === 'user' ||
        u.access === 'user' ||
        u.access === 'employee' ||
        u.access === 'careWorker'
      ).length;

      const locations = locationsRes.data?.data || locationsRes.data || [];
      console.log('Total locations fetched:', locations.length);
      const totalLocations = Array.isArray(locations) ? locations.length : 0;

      // Calculate this week's shifts
      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });
      const allSchedules = schedulesRes.data?.data || [];
      console.log('Total schedules fetched:', allSchedules.length);
      
      const thisWeekSchedules = Array.isArray(allSchedules) ? allSchedules.filter(schedule => {
        const scheduleDate = new Date(schedule.date);
        return scheduleDate >= weekStart && scheduleDate <= weekEnd;
      }) : [];

      // Calculate attendance rate from schedules (completed vs total)
      const completedSchedules = allSchedules.filter(s => {
        const scheduleDate = new Date(s.date);
        const now = new Date();
        return scheduleDate < now && s.status === 'completed';
      }).length;
      const totalPastSchedules = allSchedules.filter(s => {
        const scheduleDate = new Date(s.date);
        const now = new Date();
        return scheduleDate < now;
      }).length;
      const attendanceRate = totalPastSchedules > 0 
        ? Math.round((completedSchedules / totalPastSchedules) * 100) 
        : 0;

      const finalData = {
        totalEmployees,
        totalLocations,
        activeSchedules: thisWeekSchedules.length,
        attendanceRate,
      };
      
      console.log('Final dashboard data:', finalData);
      console.log('Total Employees:', totalEmployees);
      console.log('Total Locations:', totalLocations);
      console.log('This Week Schedules:', thisWeekSchedules.length);
      console.log('Attendance Rate:', attendanceRate + '%');
      setDashboardData(finalData);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', error.response?.data || error.message);
      console.error('Error stack:', error.stack);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayData = async () => {
    try {
      const response = await fetchSchedulesByWeek(today);
      const allSchedules = response.data?.data || [];
      
      // Filter today's schedules
      const todaySchedules = allSchedules.filter(s => s.date === today);
      const totalShifts = todaySchedules.length;
      
      // Calculate active shifts (in progress)
      const now = new Date();
      const activeShifts = todaySchedules.filter(s => {
        if (!s.start || !s.end) return false;
        const startTime = new Date(`${s.date}T${s.start}:00`);
        const endTime = new Date(`${s.date}T${s.end}:00`);
        return now >= startTime && now <= endTime;
      }).length;
      
      // For now, set these to 0 as we don't have the data
      setTodayData({
        totalShifts,
        activeShifts,
        clockedIn: 0,
        expectedToday: totalShifts,
        onBreak: 0,
        tasksDue: 0,
        tasksCompleted: 0,
      });
      
      console.log('Today data calculated:', {
        totalShifts,
        activeShifts,
        expectedToday: totalShifts
      });
    } catch (error) {
      console.error('Error calculating today data:', error);
      setTodayData({
        totalShifts: 0,
        activeShifts: 0,
        clockedIn: 0,
        expectedToday: 0,
        onBreak: 0,
        tasksDue: 0,
        tasksCompleted: 0,
      });
    }
  };

  const getScheduleStatus = (schedule) => {
    if (!schedule.start || !schedule.end || !schedule.date) {
      return 'Unknown';
    }
    try {
      const currentTime = new Date();
      const startDateTime = new Date(`${schedule.date}T${schedule.start}:00.000Z`);
      let endDate = new Date(schedule.date);
      if (schedule.end < schedule.start) {
        endDate.setDate(endDate.getDate() + 1);
      }
      const endDateTime = new Date(`${endDate.toISOString().split('T')[0]}T${schedule.end}:00.000Z`);

      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        return 'Unknown';
      }
      if (endDateTime < currentTime) return 'Completed';
      if (currentTime < startDateTime) return 'Upcoming';
      if (currentTime >= startDateTime && currentTime < endDateTime) return 'In Progress';
      return 'Unknown';
    } catch (e) {
      return 'Unknown';
    }
  };

  const fetchWeeklySchedules = async () => {
    try {
      const response = await fetchSchedulesByWeek(today);
      const allSchedules = response.data.data || [];

      // Filter for upcoming schedules in the next 7 days
      const todayDateObj = parse(today, 'yyyy-MM-dd', new Date());
      const sevenDaysFromNow = addDays(todayDateObj, 7);

      const upcomingSchedules = allSchedules
        .filter(schedule => {
          const scheduleDate = parse(schedule.date, 'yyyy-MM-dd', new Date());
          const status = getScheduleStatus({
            id: schedule._id,
            start: schedule.start,
            end: schedule.end,
            date: schedule.date
          });
          
          const isTodayOrFuture = isAfter(scheduleDate, todayDateObj) || format(scheduleDate, 'yyyy-MM-dd') === format(todayDateObj, 'yyyy-MM-dd');
          const isWithinSevenDays = isBefore(scheduleDate, sevenDaysFromNow);
          
          return status === 'Upcoming' && isTodayOrFuture && isWithinSevenDays;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      // Group by location
      const groupedByLocation = {};
      for (const schedule of upcomingSchedules) {
        const locationId = schedule.location?._id || schedule.location || 'unknown';
        const locationName = schedule.location?.name || schedule.location || 'Unknown Location';

        if (!groupedByLocation[locationId]) {
          groupedByLocation[locationId] = {
            locationId,
            locationName,
            shifts: [],
          };
        }

        const careWorkerNames = Array.isArray(schedule.careWorker)
          ? schedule.careWorker.map(cw =>
              typeof cw === 'object' && (cw?.firstName || cw?.lastName)
                ? `${cw.firstName || ''} ${cw.lastName || ''}`.trim()
                : 'Unknown'
            ).filter(Boolean)
          : (schedule.careWorker?.firstName
            ? [`${schedule.careWorker.firstName} ${schedule.careWorker.lastName || ''}`.trim()]
            : ['Unknown']);

        groupedByLocation[locationId].shifts.push({
          id: schedule._id,
          userName: careWorkerNames.join(', '),
          role: schedule.careWorker?.[0]?.role || schedule.careWorker?.role || 'Care Worker',
          date: schedule.date,
          start: schedule.start,
          end: schedule.end,
          status: 'confirmed'
        });
      }

      setWeeklySchedules(Object.values(groupedByLocation));
    } catch (error) {
      console.error('Error fetching weekly schedules:', error);
    }
  };

  const fetchTodaySchedules = async (date = selectedDate) => {
    try {
      const response = await fetchSchedulesByWeek(date);
      const allSchedules = response.data.data || [];

      // Filter for the selected date's schedules only
      const daySchedules = allSchedules.filter(schedule => schedule.date === date);

      // Group schedules by location
      const groupedByLocation = {};
      for (const schedule of daySchedules) {
        const locationId = schedule.location?._id || schedule.location || 'unknown';
        const locationName = schedule.location?.name || schedule.location || 'Unknown Location';

        if (!groupedByLocation[locationId]) {
          groupedByLocation[locationId] = {
            locationId,
            locationName,
            shifts: [],
          };
        }

        if (schedule.careWorker && Array.isArray(schedule.careWorker)) {
          schedule.careWorker.forEach((worker) => {
            groupedByLocation[locationId].shifts.push({
              id: `${schedule._id}-${worker._id || worker}`,
              worker: worker.firstName && worker.lastName
                ? `${worker.firstName} ${worker.lastName}`
                : 'Unknown Worker',
              role: worker.role || 'Care Worker',
              time: `${schedule.start} - ${schedule.end}`,
            });
          });
        } else if (schedule.careWorker) {
          const worker = schedule.careWorker;
          groupedByLocation[locationId].shifts.push({
            id: schedule._id,
            worker: worker.firstName && worker.lastName
              ? `${worker.firstName} ${worker.lastName}`
              : 'Unknown Worker',
            role: worker.role || 'Care Worker',
            time: `${schedule.start} - ${schedule.end}`,
          });
        } else {
          groupedByLocation[locationId].shifts.push({
            id: schedule._id,
            worker: 'Unassigned',
            role: 'N/A',
            time: `${schedule.start} - ${schedule.end}`,
          });
        }
      }

      setTodaySchedules(Object.values(groupedByLocation));
    } catch (error) {
      console.error('Error fetching today schedules:', error);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
    fetchTodayData();
    fetchWeeklySchedules();
    fetchTodaySchedules(selectedDate);
  };

  const handleDateChange = (direction) => {
    const currentDate = new Date(selectedDate);
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else if (direction === 'next') {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setSelectedDate(format(currentDate, 'yyyy-MM-dd'));
  };

  const handleDateSelect = (e) => {
    setSelectedDate(e.target.value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
      {/* Header with Refresh */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Dashboard</h1>
          <p className="text-gray-600">Welcome back, {user?.firstName}! Here's your real-time organization overview</p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
        >
          <FiRefreshCw className="mr-2" />
          Refresh
        </button>
      </div>

      {/* Stats Grid - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Employees"
          value={dashboardData?.totalEmployees || 0}
          subtitle="Active staff members"
          icon={FiUsers}
          color="text-blue-600"
        />
        <StatCard
          title="Active Locations"
          value={dashboardData?.totalLocations || 0}
          subtitle="Service locations"
          icon={FiMapPin}
          color="text-green-600"
        />
        <StatCard
          title="This Week's Shifts"
          value={dashboardData?.activeSchedules || 0}
          subtitle="All locations combined"
          icon={FiCalendar}
          color="text-purple-600"
        />
        <StatCard
          title="Attendance Rate"
          value={`${dashboardData?.attendanceRate || 0}%`}
          subtitle="This month"
          icon={FiActivity}
          color="text-emerald-600"
        />
      </div>

      {/* What's Happening Today */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 mb-8 text-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold flex items-center">
            <FiSun className="mr-3 h-7 w-7" />
            What's Happening Today
          </h3>
          <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
            {format(new Date(), 'EEEE, MMMM dd, yyyy')}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <p className="text-sm font-medium opacity-90 mb-1">Total Shifts</p>
            <p className="text-3xl font-bold">{todayData?.totalShifts || 0}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <p className="text-sm font-medium opacity-90 mb-1">In Progress</p>
            <p className="text-3xl font-bold">{todayData?.activeShifts || 0}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <p className="text-sm font-medium opacity-90 mb-1">Clocked In</p>
            <p className="text-3xl font-bold">{todayData?.clockedIn || 0}</p>
            <p className="text-xs opacity-75">of {todayData?.expectedToday || 0}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <p className="text-sm font-medium opacity-90 mb-1">Tasks Due</p>
            <p className="text-3xl font-bold">{todayData?.tasksDue || 0}</p>
          </div>
        </div>
      </div>

      {/* Schedule for Present Date */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <h3 className="text-2xl font-bold text-gray-900 flex items-center">
            <FiCalendar className="mr-3 text-blue-600" />
            Schedule by Location
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDateChange('prev')}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <FiChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateSelect}
              className="px-4 py-2 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 font-medium"
            />
            <button
              onClick={() => handleDateChange('next')}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <FiChevronRight className="w-5 h-5 text-gray-700" />
            </button>
            {selectedDate !== today && (
              <button
                onClick={() => setSelectedDate(today)}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Today
              </button>
            )}
          </div>
        </div>
        
        {todaySchedules && todaySchedules.length > 0 ? (
          <div className="space-y-6">
            {todaySchedules.map((location) => (
              <div key={location.locationId} className="border-l-4 border-blue-500 bg-blue-50/50 rounded-r-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <FiMapPin className="text-blue-600 mr-3 h-5 w-5" />
                    <h4 className="text-xl font-bold text-gray-900">{location.locationName}</h4>
                    <span className="ml-4 px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-full">
                      {location.shifts.length} {location.shifts.length === 1 ? 'shift' : 'shifts'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {location.shifts.map((shift) => (
                    <div key={shift.id} className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <FiUser className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{shift.worker}</p>
                            <p className="text-xs text-gray-500">{shift.role}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-gray-600 mt-2">
                        <FiClock className="mr-2 h-4 w-4" />
                        {shift.time}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FiCalendar className="mx-auto h-12 w-12 text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium">No shifts scheduled for {format(new Date(selectedDate), 'MMMM dd, yyyy')}</p>
          </div>
        )}
      </div>

      {/* Hours Breakdown & Analytics - Unified Enterprise Chart */}
      <div className="mb-8">
        <EnterpriseAnalytics />
      </div>

      {/* Upcoming Weekly Schedules */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FiCalendar className="mr-2 text-blue-600" />
            Upcoming Weekly Schedules
          </h3>
          <span className="text-sm text-gray-500">
            {format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'MMM dd')} - {format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'MMM dd, yyyy')}
          </span>
        </div>
        
        <div className="space-y-6">
          {weeklySchedules && weeklySchedules.length > 0 ? (
            weeklySchedules.map((location) => (
              <div key={location.locationId} className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <FiMapPin className="text-blue-600 mr-2" />
                    <h4 className="font-semibold text-gray-900">{location.locationName}</h4>
                    <span className="ml-3 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      {location.shifts.length} {location.shifts.length === 1 ? 'shift' : 'shifts'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {location.shifts.map((shift) => (
                    <div key={shift.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center">
                          <FiUser className="text-gray-600 mr-2 mt-1" />
                          <div>
                            <p className="font-medium text-gray-900">{shift.userName}</p>
                            <p className="text-xs text-gray-500">{shift.role}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          shift.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          shift.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {shift.status}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <FiCalendar className="mr-2 h-4 w-4" />
                        {format(new Date(shift.date), 'EEE, MMM dd')}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <FiClock className="mr-2 h-4 w-4" />
                        {shift.start} - {shift.end}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              No scheduled shifts for this week
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a
            href="/schedule"
            className="flex flex-col items-center justify-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <FiCalendar className="h-8 w-8 text-blue-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Create Schedule</span>
          </a>
          <a
            href="/people"
            className="flex flex-col items-center justify-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
          >
            <FiUsers className="h-8 w-8 text-green-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Manage Staff</span>
          </a>
          <a
            href="/timesheets"
            className="flex flex-col items-center justify-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <FiClock className="h-8 w-8 text-purple-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Timesheets</span>
          </a>
          <a
            href="/analytics"
            className="flex flex-col items-center justify-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
          >
            <FiTrendingUp className="h-8 w-8 text-orange-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Analytics</span>
          </a>
        </div>
      </div>

      {/* Alerts Section */}
      {(dashboardData?.highRiskEmployees > 0 || dashboardData?.complianceIssues > 0) && (
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FiAlertCircle className="mr-2 text-red-600" />
            Alerts & Warnings
          </h3>
          <div className="space-y-3">
            {dashboardData.highRiskEmployees > 0 && (
              <div className="flex items-center p-3 bg-red-50 rounded-lg">
                <FiAlertCircle className="text-red-600 mr-3" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {dashboardData.highRiskEmployees} employee(s) at high retention risk
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Review performance data and take action</p>
                </div>
                <a href="/analytics" className="text-sm text-blue-600 hover:underline">
                  View Details
                </a>
              </div>
            )}
            {dashboardData.complianceIssues > 0 && (
              <div className="flex items-center p-3 bg-orange-50 rounded-lg">
                <FiAlertCircle className="text-orange-600 mr-3" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {dashboardData.complianceIssues} compliance issue(s) detected
                  </p>
                  <p className="text-xs text-gray-600 mt-1">Address to maintain regulatory compliance</p>
                </div>
                <a href="/compliance" className="text-sm text-blue-600 hover:underline">
                  View Details
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardOverview;
